import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { X, Send } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { useConversation } from '@elevenlabs/react';
import { GRABAI_AGENT_ID } from '@/constants/customer-support';
import { CUSTOMER_SUPPORT_SYSTEM_PROMPT, GRAB_AGENT_FIRST_MESSAGE } from '@/constants/customer-support-prompt';
import { SimpleChatMessage } from './SimpleChatMessage';
import { SimpleTypingIndicator } from './SimpleTypingIndicator';
import toast from 'react-hot-toast';

const MODERN_COLORS = {
  background: '#FFFFFF',
  chatBg: '#F9FAFB',
  text: '#111827',
  textSecondary: '#6B7280',
  primary: '#10B981',
  primaryText: '#FFFFFF',
  border: '#E5E7EB',
  inputBg: '#FFFFFF',
  userBubble: '#111827',
  botBubble: '#F3F4F6',
};

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export const SimpleChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ElevenLabs conversation hook configured for text-only mode
  const conversation = useConversation({
    textOnly: true, // Enable text-only mode for chat interface
    onConnect: () => {
      console.log('Connected to support agent');
      setIsInitialized(true);
      setConnectionError(null);
    },
    onDisconnect: (error: any) => {
      setIsTyping(false);
      if (error) {
        console.error('Disconnected due to error:', error);
        const errorMessage =
          error?.reason || error?.message || 'Connection lost';
        setConnectionError(errorMessage);
        toast.error(`Disconnected: ${errorMessage}`);
      } else {
        toast.success('Disconnected from support agent');
      }
      setIsInitialized(false);
    },
    onMessage: ({ source, message }) => {
      console.log('Received message:', { source, message });

      if (source === 'ai' && message) {
        addMessage(message, 'bot');
        setIsTyping(false);
      } else if (source === 'user' && message) {
        console.log('User message echoed back:', message);
      }
    },
    onError: (error) => {
      console.error('Conversation error:', error);
      setIsTyping(false);
      toast.error('Connection issue. Please try again.');
    },
  });

  // Start conversation on mount
  useEffect(() => {
    let isMounted = true;

    const initializeConversation = async () => {
      if (!isMounted || conversation.status === 'connected' || isConnecting)
        return;

      try {
        setIsConnecting(true);
        console.log('Initializing conversation on mount...');

        const sessionId = await conversation.startSession({
          agentId: GRABAI_AGENT_ID,
          connectionType: 'webrtc',
          // Include any required dynamic variables here
          overrides: {
            agent: {
              prompt: { prompt: CUSTOMER_SUPPORT_SYSTEM_PROMPT },
              firstMessage: GRAB_AGENT_FIRST_MESSAGE,
            },
          },
        });

        console.log('Conversation initialized with session ID:', sessionId);

        // Send contextual update to let the agent know this is a text-based chat
        if (conversation.sendContextualUpdate) {
          conversation.sendContextualUpdate(
            'User is interacting via text-based chat interface in a support widget.',
          );
        }
      } catch (error) {
        console.error('Failed to initialize conversation:', error);
        setConnectionError('Failed to connect to support agent');
      } finally {
        if (isMounted) {
          setIsConnecting(false);
        }
      }
    };

    // Initialize after a small delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      initializeConversation();
    }, 100);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      clearTimeout(timer);

      // Only end session if component is actually unmounting (not just re-rendering)
      if (conversation.status === 'connected') {
        console.log('Component unmounting, ending session...');
        conversation.endSession().catch((err) => {
          console.error('Error ending session on unmount:', err);
        });
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const addMessage = useCallback((text: string, sender: 'user' | 'bot') => {
    const newMessage: Message = {
      id: Date.now().toString(),
      text,
      sender,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, newMessage]);
  }, []);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isConnecting) return;

    const userMessage = inputValue.trim();
    addMessage(userMessage, 'user');
    setInputValue('');
    setIsTyping(true);

    try {
      // Check if connected
      if (conversation.status !== 'connected') {
        console.error(
          'Not connected to agent. Current status:',
          conversation.status,
        );
        throw new Error('Please wait for the connection to establish');
      }

      // Send the user message
      console.log('Sending user message:', userMessage);
      conversation.sendUserMessage(userMessage);
      console.log('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      setIsTyping(false);

      if (conversation.status !== 'connected') {
        addMessage(
          'Still connecting to support agent. Please wait a moment.',
          'bot',
        );
      } else {
        addMessage(
          "I'm having trouble sending your message. Please try again.",
          'bot',
        );
      }
    }
  };

  const handleClose = async () => {
    try {
      if (conversation.status === 'connected') {
        await conversation.endSession();
      }
    } catch (error) {
      console.error('Error ending session:', error);
    }
    window.location.reload();
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 0, md: 20 },
        right: { xs: 0, md: 20 },
        width: { xs: '100%', md: 420 },
        height: { xs: '100vh', md: 600 },
        display: 'flex',
        flexDirection: 'column',
        bgcolor: MODERN_COLORS.background,
        borderRadius: { xs: 0, md: 3 },
        overflow: 'hidden',
        boxShadow: { xs: 'none', md: '0 20px 50px rgba(0,0,0,0.15)' },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${MODERN_COLORS.border}`,
          bgcolor: MODERN_COLORS.background,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor:
                conversation.status === 'connected'
                  ? MODERN_COLORS.primary
                  : '#FFA500',
              animation:
                conversation.status === 'connected'
                  ? 'pulse 2s infinite'
                  : 'none',
              '@keyframes pulse': {
                '0%': { opacity: 1 },
                '50%': { opacity: 0.5 },
                '100%': { opacity: 1 },
              },
            }}
          />
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: MODERN_COLORS.text,
            }}
          >
            Support Chat
            {(conversation.status !== 'connected' || isConnecting) && (
              <Typography
                variant="caption"
                sx={{ ml: 1, color: MODERN_COLORS.textSecondary }}
              >
                ({isConnecting ? 'connecting...' : conversation.status})
              </Typography>
            )}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ color: MODERN_COLORS.textSecondary }}
        >
          <X size={20} />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          bgcolor: MODERN_COLORS.chatBg,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <AnimatePresence>
          {messages.map((message) => (
            <SimpleChatMessage key={message.id} message={message} />
          ))}
        </AnimatePresence>

        {isTyping && <SimpleTypingIndicator />}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${MODERN_COLORS.border}`,
          bgcolor: MODERN_COLORS.background,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <input
            ref={inputRef}
            type="text"
            placeholder="Type your message..."
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              // Send user activity to prevent agent interruption while typing
              if (
                conversation.status === 'connected' &&
                conversation.sendUserActivity
              ) {
                conversation.sendUserActivity();
              }
            }}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            style={{
              flex: 1,
              border: `1px solid ${MODERN_COLORS.border}`,
              borderRadius: 8,
              padding: '12px 16px',
              fontSize: '15px',
              fontFamily: 'inherit',
              outline: 'none',
              backgroundColor: MODERN_COLORS.inputBg,
              transition: 'border-color 0.2s',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = MODERN_COLORS.primary;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = MODERN_COLORS.border;
            }}
          />
          <IconButton
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isConnecting}
            sx={{
              bgcolor:
                inputValue.trim() && !isConnecting
                  ? MODERN_COLORS.primary
                  : MODERN_COLORS.border,
              color:
                inputValue.trim() && !isConnecting
                  ? MODERN_COLORS.primaryText
                  : MODERN_COLORS.textSecondary,
              '&:hover': {
                bgcolor:
                  inputValue.trim() && !isConnecting
                    ? MODERN_COLORS.primary
                    : MODERN_COLORS.border,
                opacity: inputValue.trim() && !isConnecting ? 0.9 : 1,
              },
              '&:disabled': {
                color: MODERN_COLORS.textSecondary,
              },
              width: 44,
              height: 44,
            }}
          >
            <Send size={20} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
